import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { 
  Search, Loader2, MapPin, Clock, User, Calendar, ExternalLink, 
  Link2, Unlink, Trash2, Check, X, Download, Copy, CheckCircle, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';

import { API_URL } from '../../utils/api';

export function AdminScrapedClassesTab() {
  const [classes, setClasses] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [showBookmarkletDialog, setShowBookmarkletDialog] = useState(false);
  const [copiedBookmarklet, setCopiedBookmarklet] = useState(false);
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [editForm, setEditForm] = useState({
    instructor_name: '',
    day: '',
    time: '',
    level: '',
    venue: '',
    location: '',
    instructor_profile_url: '',
    instructor_image: ''
  });
  const [saving, setSaving] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 100;

  useEffect(() => {
    fetchClasses();
  }, [statusFilter, assignedFilter, currentPage]);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/scraped-classes?limit=${pageSize}&offset=${currentPage * pageSize}`;
      
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (assignedFilter === 'assigned') {
        url += `&assigned=true`;
      } else if (assignedFilter === 'unassigned') {
        url += `&assigned=false`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setClasses(data.classes || []);
        setClubs(data.clubs || []);
        setTotalCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchClasses();
  };

  const handleAssignClub = async (classId, clubId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/scraped-classes/${classId}/assign`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ club_id: clubId })
      });
      
      if (res.ok) {
        toast.success('Class assigned to club');
        fetchClasses();
      } else {
        toast.error('Failed to assign class');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleUnassign = async (classId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/scraped-classes/${classId}/unassign`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Class unassigned');
        fetchClasses();
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleDelete = async (classId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/scraped-classes/${classId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Class deleted');
        fetchClasses();
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleApprove = async (classId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/scraped-classes/${classId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Class approved');
        fetchClasses();
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const handleEditClick = (cls) => {
    setEditingClass(cls);
    setEditForm({
      instructor_name: cls.instructor_name || '',
      day: cls.day || '',
      time: cls.time || '',
      level: cls.level || '',
      venue: cls.venue || '',
      location: cls.location || '',
      instructor_profile_url: cls.instructor_profile_url || '',
      instructor_image: cls.instructor_image || ''
    });
    setShowEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!editingClass) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/scraped-classes/${editingClass.class_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      
      if (res.ok) {
        toast.success('Class updated successfully');
        setShowEditDialog(false);
        setEditingClass(null);
        fetchClasses();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update class');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  // Generate bookmarklet code - minified single line for browser compatibility
  // Tries multiple table selectors, shows debug info if nothing found
  const bookmarkletCode = `javascript:(function(){var r=null,s=['%23eventslist tbody tr','table.dataTable tbody tr','.table tbody tr','table tbody tr'];for(var i=0;i<s.length;i++){r=document.querySelectorAll(s[i]);if(r&&r.length>0)break}if(!r||r.length===0){var t=document.querySelectorAll('table');alert('DEBUG: Found '+t.length+' tables. IDs: '+Array.from(t).map(function(x){return x.id||x.className||'(none)'}).join(', '));return}var c=[];r.forEach(function(row){var cells=row.querySelectorAll('td');if(cells.length>=6){var ins='',day='',lv='',tm='',ven='',loc='';if(cells.length>=9){var a=cells[1].querySelector('a');ins=a?a.textContent.trim():cells[1].textContent.trim();day=cells[3].textContent.trim().replace(/\\s+/g,' ');var sp=cells[4].querySelector('.label,span');lv=sp?sp.textContent.trim():cells[4].textContent.trim();tm=cells[5].textContent.trim();ven=cells[6].textContent.trim();loc=cells[7].textContent.trim()}else{ins=cells[0].textContent.trim();day=cells[1].textContent.trim();lv=cells[2].textContent.trim();tm=cells[3].textContent.trim();ven=cells[4].textContent.trim();loc=cells[5].textContent.trim()}if(ins||ven){c.push({instructor_name:ins,day:day,level:lv,time:tm,venue:ven,location:loc})}}});if(c.length===0){alert('No classes parsed from '+r.length+' rows');return}var d=JSON.stringify({classes:c,source_page_url:location.href,scraped_at:new Date().toISOString()});navigator.clipboard.writeText(d).then(function(){alert('SUCCESS! '+c.length+' classes copied to clipboard')}).catch(function(e){prompt('Clipboard failed. Copy this:',d)})})();`;

  const copyBookmarklet = () => {
    navigator.clipboard.writeText(bookmarkletCode);
    setCopiedBookmarklet(true);
    toast.success('Bookmarklet code copied!');
    setTimeout(() => setCopiedBookmarklet(false), 3000);
  };
  
  const [showManualPaste, setShowManualPaste] = useState(false);
  const [manualPasteData, setManualPasteData] = useState('');
  
  const handlePasteImport = async () => {
    try {
      let clipboardText;
      
      try {
        clipboardText = await navigator.clipboard.readText();
      } catch (clipboardError) {
        // Clipboard access denied - show manual paste dialog
        setShowManualPaste(true);
        return;
      }
      
      if (!clipboardText || clipboardText.trim() === '') {
        setShowManualPaste(true);
        return;
      }
      
      await processImportData(clipboardText);
    } catch (error) {
      if (error.name === 'SyntaxError') {
        toast.error('Invalid JSON data. Make sure you ran the bookmarklet first.');
        setShowManualPaste(true);
      } else {
        toast.error('Failed to read clipboard. Try the manual paste option.');
        setShowManualPaste(true);
      }
    }
  };
  
  const handleManualPasteSubmit = async () => {
    if (!manualPasteData.trim()) {
      toast.error('Please paste the class data');
      return;
    }
    await processImportData(manualPasteData);
    setShowManualPaste(false);
    setManualPasteData('');
  };
  
  const processImportData = async (jsonText) => {
    try {
      const data = JSON.parse(jsonText);
      
      if (!data.classes || !Array.isArray(data.classes)) {
        toast.error('Invalid data format. Please run the bookmarklet first.');
        return;
      }
      
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/scraped-classes/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: jsonText
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Import complete! ${result.imported} new classes, ${result.skipped} duplicates skipped`);
        fetchClasses();
        setShowBookmarkletDialog(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      if (error.name === 'SyntaxError') {
        toast.error('Invalid JSON. Make sure you copied the data from the bookmarklet alert.');
      } else {
        toast.error('Import failed: ' + error.message);
      }
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      assigned: 'bg-violet-100 text-violet-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const getLevelBadge = (level) => {
    const l = level?.toLowerCase() || '';
    if (l.includes('absolute') || l.includes('beginner')) return 'bg-green-100 text-green-700';
    if (l.includes('improver')) return 'bg-blue-100 text-blue-700';
    if (l.includes('intermediate')) return 'bg-amber-100 text-amber-700';
    if (l.includes('advanced')) return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Scraped Classes</h2>
          <p className="text-slate-500 mt-1">
            Import and manage classes from Everything Line Dance
          </p>
        </div>
        <Button onClick={() => setShowBookmarkletDialog(true)} className="bg-violet-600 hover:bg-violet-500">
          <Download className="w-4 h-4 mr-2" />
          Get Bookmarklet
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search instructor, venue, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={assignedFilter} onValueChange={setAssignedFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Assignment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSearch} variant="outline">
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
        </div>
      </div>

      {/* Classes List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {classes.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No scraped classes found</p>
            <p className="text-sm">Use the bookmarklet to import classes from Everything Line Dance</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Instructor</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Day/Time</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Level</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Venue</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Assign to Club</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {classes.map((cls) => (
                  <tr key={cls.class_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {cls.instructor_image && (
                          <img 
                            src={cls.instructor_image} 
                            alt="" 
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <div className="font-medium text-slate-900">{cls.instructor_name}</div>
                          {cls.instructor_profile_url && (
                            <a 
                              href={cls.instructor_profile_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-violet-600 hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Profile
                            </a>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{cls.day}</div>
                      <div className="text-sm text-slate-500">{cls.time}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={getLevelBadge(cls.level)}>
                        {cls.level}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{cls.venue}</div>
                      <div className="text-xs text-slate-500 max-w-[200px] truncate">{cls.location}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={getStatusBadge(cls.status)}>
                        {cls.status}
                      </Badge>
                      {cls.assigned_club_name && (
                        <div className="text-xs text-slate-500 mt-1">
                          {cls.assigned_club_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {cls.assigned_club_id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">{cls.assigned_club_name}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUnassign(cls.class_id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Unlink className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Select onValueChange={(clubId) => handleAssignClub(cls.class_id, clubId)}>
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Select club..." />
                          </SelectTrigger>
                          <SelectContent>
                            {clubs.map((club) => (
                              <SelectItem key={club.club_id} value={club.club_id}>
                                {club.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditClick(cls)}
                          className="text-violet-600 hover:text-violet-700"
                          title="Edit class details"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {cls.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleApprove(cls.class_id)}
                            className="text-green-600 hover:text-green-700"
                            title="Approve class"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" title="Delete class">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Class?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete this scraped class.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDelete(cls.class_id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-violet-50 rounded-xl p-4 border border-violet-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-violet-600" />
            <div>
              <p className="font-medium text-violet-900">
                {totalCount} total classes {classes.length < totalCount && `(showing ${classes.length})`}
              </p>
              <p className="text-sm text-violet-700">
                {classes.filter(c => c.status === 'assigned').length} assigned, {' '}
                {classes.filter(c => c.status === 'pending').length} pending review
              </p>
            </div>
          </div>
          
          {/* Pagination */}
          {totalCount > pageSize && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                Page {currentPage + 1} of {Math.ceil(totalCount / pageSize)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={(currentPage + 1) * pageSize >= totalCount}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Bookmarklet Dialog */}
      <Dialog open={showBookmarkletDialog} onOpenChange={setShowBookmarkletDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Class Import Bookmarklet</DialogTitle>
            <DialogDescription>
              Use this bookmarklet to import classes from Everything Line Dance
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-100 rounded-lg p-4">
              <h4 className="font-medium mb-2">How to use:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600">
                <li>Drag the button below to your bookmarks bar (or copy the code)</li>
                <li>Go to <a href="https://www.everythinglinedance.com/findclass/" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">Everything Line Dance Class Finder</a></li>
                <li>Search for classes in your area</li>
                <li>Click the bookmarklet - it will copy the class data</li>
                <li>Come back here and click "Paste Import Data" below</li>
              </ol>
            </div>

            <div className="flex flex-col gap-3">
              <div className="text-center">
                <a
                  href={bookmarkletCode}
                  onClick={(e) => e.preventDefault()}
                  className="inline-flex items-center gap-2 bg-violet-600 text-white px-6 py-3 rounded-lg font-medium cursor-move"
                  draggable="true"
                >
                  <Download className="w-5 h-5" />
                  Import LDC Classes
                </a>
                <p className="text-xs text-slate-500 mt-2">← Drag this to your bookmarks bar</p>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-3 text-center">After running the bookmarklet on Everything Line Dance:</p>
                <div className="flex justify-center">
                  <Button onClick={handlePasteImport} size="lg" className="bg-emerald-600 hover:bg-emerald-500">
                    <Download className="w-5 h-5 mr-2" />
                    Paste Import Data
                  </Button>
                </div>
              </div>

              <div className="border-t pt-3">
                <p className="text-sm text-slate-500 mb-2">Or copy the bookmarklet code manually:</p>
                <div className="relative">
                  <textarea
                    readOnly
                    value={bookmarkletCode}
                    className="w-full h-20 p-3 text-xs font-mono bg-slate-900 text-green-400 rounded-lg"
                  />
                  <Button
                    size="sm"
                    onClick={copyBookmarklet}
                    className="absolute top-2 right-2"
                  >
                    {copiedBookmarklet ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookmarkletDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Class Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Class Details</DialogTitle>
            <DialogDescription>
              Update the class information below
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="instructor_name">Instructor Name *</Label>
              <Input
                id="instructor_name"
                value={editForm.instructor_name}
                onChange={(e) => setEditForm({...editForm, instructor_name: e.target.value})}
                placeholder="e.g. John Smith"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="day">Day *</Label>
              <Select 
                value={editForm.day} 
                onValueChange={(value) => setEditForm({...editForm, day: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Monday">Monday</SelectItem>
                  <SelectItem value="Tuesday">Tuesday</SelectItem>
                  <SelectItem value="Wednesday">Wednesday</SelectItem>
                  <SelectItem value="Thursday">Thursday</SelectItem>
                  <SelectItem value="Friday">Friday</SelectItem>
                  <SelectItem value="Saturday">Saturday</SelectItem>
                  <SelectItem value="Sunday">Sunday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="time">Time *</Label>
              <Input
                id="time"
                value={editForm.time}
                onChange={(e) => setEditForm({...editForm, time: e.target.value})}
                placeholder="e.g. 19:30-21:00"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="level">Level *</Label>
              <Select 
                value={editForm.level} 
                onValueChange={(value) => setEditForm({...editForm, level: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ABSOLUTE BEGINNER">Absolute Beginner</SelectItem>
                  <SelectItem value="BEGINNER">Beginner</SelectItem>
                  <SelectItem value="IMPROVER">Improver</SelectItem>
                  <SelectItem value="EASY INTERMEDIATE">Easy Intermediate</SelectItem>
                  <SelectItem value="INTERMEDIATE">Intermediate</SelectItem>
                  <SelectItem value="ADVANCED">Advanced</SelectItem>
                  <SelectItem value="SOCIAL">Social</SelectItem>
                  <SelectItem value="MIXED">Mixed Levels</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label htmlFor="venue">Venue *</Label>
              <Input
                id="venue"
                value={editForm.venue}
                onChange={(e) => setEditForm({...editForm, venue: e.target.value})}
                placeholder="e.g. Community Hall"
              />
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label htmlFor="location">Full Address</Label>
              <Input
                id="location"
                value={editForm.location}
                onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                placeholder="e.g. 123 High Street, Town, County, AB1 2CD"
              />
            </div>
            
            <div className="col-span-2 border-t pt-4 mt-2">
              <p className="text-sm text-slate-500 mb-3">Optional: Instructor Links</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="instructor_profile_url">Profile URL</Label>
              <Input
                id="instructor_profile_url"
                value={editForm.instructor_profile_url}
                onChange={(e) => setEditForm({...editForm, instructor_profile_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="instructor_image">Image URL</Label>
              <Input
                id="instructor_image"
                value={editForm.instructor_image}
                onChange={(e) => setEditForm({...editForm, instructor_image: e.target.value})}
                placeholder="https://..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Paste Dialog */}
      <Dialog open={showManualPaste} onOpenChange={setShowManualPaste}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Paste Class Data Manually</DialogTitle>
            <DialogDescription>
              Clipboard access was denied. Please paste the JSON data from the bookmarklet below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Tip:</strong> After running the bookmarklet, the data is copied to your clipboard. 
              Press Ctrl+V (or Cmd+V on Mac) in the box below to paste it.
            </div>
            
            <textarea
              className="w-full h-40 p-3 text-sm font-mono bg-slate-100 rounded-lg border border-slate-300 focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
              placeholder='Paste the JSON data here... It should start with {"classes":[...'
              value={manualPasteData}
              onChange={(e) => setManualPasteData(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowManualPaste(false); setManualPasteData(''); }}>
              Cancel
            </Button>
            <Button onClick={handleManualPasteSubmit}>
              Import Classes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminScrapedClassesTab;
